<script setup lang="ts">
  import { useDropZone, useEventListener, useFileDialog } from '@vueuse/core';
  import { computed, nextTick, provide, ref, watch } from 'vue';
  import type { Ref } from 'vue';

  import { Attachment } from '@stuut/api/models';
  import { formatFileSize } from '@stuut/common/src/utils';
  import { toast } from '@ui/components/sonner';

  import { FileDropzoneKey } from '@/components/file-upload-dropzone';
  import {
    ACCEPTED_FILE_EXTENSIONS,
    ACCEPTED_FILE_TYPES,
    MAX_FILE_AMOUNT,
  } from '@/constants/Attachments';

  interface Props {
    allowDuplicates?: boolean;
    // Bytes already attached (consumer-owned). Only used when `maxTotalSize` is set.
    currentAttachmentsSize?: number;
    disabled?: boolean;
    fileTypes?: string[];
    maxNumAttachments?: number;
    // When set, attachments are gated by combined byte size instead of file count.
    maxTotalSize?: number;
    multiple?: boolean;
    preventDefaultForUnhandled?: boolean;
  }

  const props = withDefaults(defineProps<Props>(), {
    multiple: true,
    preventDefaultForUnhandled: true,
  });

  const emit = defineEmits<{
    (e: 'onNewFileAttachments', attachments: Attachment[], args: Record<string, any>): void;
  }>();

  const useDropZoneRef = ref<HTMLDivElement>();
  const fileAttachments = ref<Attachment[]>([]);
  const isOverDropZone = ref(false);
  // Native drag events fire for ANY drag — including in-page element drags such
  // as vuedraggable/Sortable reordering. Track whether the drag actually carries
  // files (via the `Files` dataTransfer sentinel) so the overlay only reacts to
  // real uploads. MIME type can't disambiguate here: Sortable tags its drags as
  // `text/plain`, which is also a valid upload type.
  const isDraggingFiles = ref(false);

  const allowedNumAttachments = computed(() => {
    return (props.maxNumAttachments || MAX_FILE_AMOUNT) - (fileAttachments.value?.length || 0);
  });

  const allowedFileTypes = computed(
    () => props.fileTypes || Object.keys(ACCEPTED_FILE_TYPES) || [],
  );

  const singleFileMode = computed(() => !props.multiple);

  const clearFileAttachments = () => {
    fileAttachments.value = [];
  };

  const removeFileAttachment = (attachment: Attachment) => {
    fileAttachments.value = fileAttachments.value.filter((att) => {
      return !(att === attachment || (attachment.id && att.id && att.id === attachment.id));
    });
  };

  const isSingleUploadLimitReached = computed(
    () => Boolean(singleFileMode.value) && Boolean(fileAttachments.value?.length),
  );

  const setupDropZone = (useDropZoneRef: Ref<HTMLDivElement>) => {
    useEventListener(useDropZoneRef, ['dragenter', 'dragover'], (event: DragEvent) => {
      isDraggingFiles.value = event.dataTransfer?.types.includes('Files') ?? false;
    });

    const { isOverDropZone: newIsOverDropZone } = useDropZone(useDropZoneRef, {
      multiple: props.multiple,
      onDrop,
      preventDefaultForUnhandled: props.preventDefaultForUnhandled,
    });

    watch(newIsOverDropZone, (value) => {
      // Only show for genuine file drags when not disabled; always allow false.
      // This ignores in-page element drags (e.g. vuedraggable segment reorder).
      isOverDropZone.value = value && !props.disabled && isDraggingFiles.value;
    });
  };

  const {
    onChange,
    open: openFileInput,
    reset: resetFileInput,
  } = useFileDialog({
    accept: [...allowedFileTypes.value, ...ACCEPTED_FILE_EXTENSIONS].join(','),
    multiple: props.multiple,
  });

  const onFilesAttached = (files: FileList | File[]) => {
    if (props.maxTotalSize != null) {
      const incomingSize = Array.from(files).reduce((sum, file) => sum + (file.size || 0), 0);
      if ((props.currentAttachmentsSize || 0) + incomingSize > props.maxTotalSize) {
        toast.error(`Attachments can't exceed ${formatFileSize(props.maxTotalSize)} in total.`);
        return;
      }
    } else if (allowedNumAttachments.value < files.length) {
      toast.error(`You may only attach up to ${allowedNumAttachments.value} more files.`);
      return;
    }

    const newAttachments: Attachment[] = [];

    Array.from(files).forEach((file) => {
      // Check if file type is allowed (fall back to extension for files where
      // the browser reports a generic MIME like application/octet-stream)
      const ext = file.name.includes('.') ? `.${file.name.split('.').pop()!.toLowerCase()}` : '';
      if (!allowedFileTypes.value?.includes(file.type) && !ACCEPTED_FILE_EXTENSIONS.has(ext)) {
        toast.error(`File type not allowed: ${file.name}`);
        return;
      }

      // Check if file with same name already exists
      const existingFile = fileAttachments.value?.find((att) => att.name === file.name);

      if (existingFile && !props.allowDuplicates) {
        toast.error(`File already exists: ${file.name}`);
        return;
      }

      const attachment = new Attachment({
        mime_type: file.type,
        name: file.name,
      });

      attachment._file = file;
      newAttachments.push(attachment);
    });

    if (newAttachments.length > 0) {
      fileAttachments.value = [...(fileAttachments.value || []), ...newAttachments];

      emit('onNewFileAttachments', newAttachments, { resetFileInput });
    }
  };

  function onDrop(files: File[] | null) {
    if (props.disabled || isSingleUploadLimitReached.value) return;

    if (files) {
      const fileList = new DataTransfer();
      files.forEach((file) => fileList.items.add(file));
      onFilesAttached(fileList.files);
    }
  }

  onChange((files) => {
    if (!files || props.disabled || isSingleUploadLimitReached.value) return;
    onFilesAttached(files);
    nextTick(() => resetFileInput());
  });

  const context = {
    allowedNumAttachments,
    clearFileAttachments,
    fileAttachments,
    isOverDropZone,
    isSingleUploadLimitReached,
    onFilesAttached,
    openFileInput,
    removeFileAttachment,
    resetFileInput,
    setupDropZone,
    useDropZoneRef,
    disabled: computed(() => props.disabled),
  };

  provide(FileDropzoneKey, context);

  defineExpose({
    clearFileAttachments,
    fileAttachments,
    isOverDropZone,
    isSingleUploadLimitReached,
    openFileInput,
    removeFileAttachment,
    resetFileInput,
    setupDropZone,
  });
</script>

<template>
  <div ref="useDropZoneRef">
    <slot
      v-bind="{
        openFileInput,
        resetFileInput,
        isOverDropZone,
        fileAttachments,
        clearFileAttachments,
        removeFileAttachment,
        isSingleUploadLimitReached,
      }"
    />
  </div>
</template>

<style scoped></style>
